import React, { useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Logo } from "@/components/Logo";
import { ThemeToggle } from "@/components/ThemeToggle";
import { GraduationCap, CheckCircle2, BookOpen, ShieldCheck, AlertCircle, Loader2 } from "lucide-react";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function Login() {
  const params = new URLSearchParams(window.location.search);
  const error = params.get("error");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const handleLogin = () => {
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  const handlePasswordLogin = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      await api.post("/auth/login", { username, password });
      window.location.href = "/dashboard";
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Invalid username or password");
      setLoading(false);
    }
  };

  const features = [
    { icon: CheckCircle2, title: "Digital Checklists", desc: "Structured, multi-day onboarding with autosave." },
    { icon: BookOpen, title: "Training Library", desc: "Every SOP and handbook in one searchable place." },
    { icon: ShieldCheck, title: "Signed Records", desc: "Auto-generated PDF completion records to HR." },
  ];

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="absolute top-4 right-4 z-10"><ThemeToggle /></div>

      {/* Brand panel */}
      <div className="relative hidden lg:flex flex-col justify-between bg-primary text-primary-foreground p-12 overflow-hidden">
        <div className="absolute -right-24 -top-24 h-96 w-96 rounded-full bg-accent/20 blur-3xl" />
        <div className="absolute -left-16 bottom-10 h-72 w-72 rounded-full bg-accent/10 blur-3xl" />
        <div className="relative flex items-center gap-3">
          <div className="flex items-center justify-center h-11 w-11 rounded-full bg-accent/20 border border-accent/30">
            <GraduationCap size={24} />
          </div>
          <div>
            <div className="font-heading font-extrabold text-lg tracking-tight">THE LOTUS ACADEMY</div>
            <div className="text-[10px] uppercase tracking-[0.22em] opacity-70">Excellence in Education</div>
          </div>
        </div>
        <div className="relative space-y-8">
          <h1 className="font-heading text-4xl xl:text-5xl font-extrabold leading-tight tracking-tight">
            Track every training,<br />from day one to sign-off.
          </h1>
          <p className="text-primary-foreground/70 text-lg max-w-md">
            The internal tool your managers and trainers use to run onboarding, complete checklists, and keep HR in the loop.
          </p>
          <div className="space-y-5 pt-4">
            {features.map((f) => (
              <div key={f.title} className="flex items-start gap-4">
                <div className="mt-0.5 flex items-center justify-center h-10 w-10 rounded-lg bg-accent/20 border border-accent/30 shrink-0">
                  <f.icon size={20} />
                </div>
                <div>
                  <div className="font-heading font-semibold">{f.title}</div>
                  <div className="text-sm text-primary-foreground/60">{f.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="relative text-xs text-primary-foreground/50">Internal use only · Authorized staff access</div>
      </div>

      {/* Sign-in panel */}
      <div className="flex flex-col items-center justify-center p-8">
        <div className="w-full max-w-sm space-y-8">
          <div className="lg:hidden flex justify-center"><Logo size="lg" /></div>
          <div className="space-y-2 text-center lg:text-left">
            <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Welcome back</h2>
            <p className="text-muted-foreground">Sign in with your Purple Lotus Google account to continue.</p>
          </div>
          {error && (
            <div className="flex items-start gap-2 text-sm rounded-lg p-3 bg-destructive/10 text-destructive" data-testid="login-error">
              <AlertCircle size={16} className="mt-0.5 shrink-0" />
              <span>
                {error === "domain"
                  ? "Access is restricted to @purplelotuspatientcenter.com accounts. Please sign in with your company email."
                  : "We couldn't sign you in. Please try again."}
              </span>
            </div>
          )}
          <Button
            data-testid="login-button"
            onClick={handleLogin}
            className="w-full h-12 text-base gap-3 rounded-xl active:scale-[0.99] transition-transform"
          >
            <svg width="18" height="18" viewBox="0 0 24 24"><path fill="currentColor" d="M12 11v2.8h4a3.9 3.9 0 0 1-1.7 2.6v2.1h2.7c1.6-1.5 2.5-3.6 2.5-6.1c0-.6 0-1.1-.2-1.6H12z"/><path fill="currentColor" d="M12 20c2.3 0 4.2-.8 5.6-2l-2.7-2.1c-.8.5-1.7.8-2.9.8c-2.2 0-4.1-1.5-4.8-3.5H4.4v2.2A8 8 0 0 0 12 20z"/><path fill="currentColor" d="M7.2 11.2a4.8 4.8 0 0 1 0-3v-2.2H4.4a8 8 0 0 0 0 7.2l2.8-2z"/><path fill="currentColor" d="M12 6.6c1.2 0 2.3.4 3.2 1.3l2.4-2.4A8 8 0 0 0 4.4 6l2.8 2.2C7.9 6.6 9.8 6.6 12 6.6z"/></svg>
            Continue with Google
          </Button>
          <div className="flex items-center gap-3 text-xs text-muted-foreground">
            <div className="h-px flex-1 bg-border" /> Trainers &amp; Trainees <div className="h-px flex-1 bg-border" />
          </div>
          <form onSubmit={handlePasswordLogin} className="space-y-3" data-testid="password-login-form">
            {err && (
              <div className="flex items-start gap-2 text-sm rounded-lg p-3 bg-destructive/10 text-destructive" data-testid="password-login-error">
                <AlertCircle size={16} className="mt-0.5 shrink-0" /><span>{err}</span>
              </div>
            )}
            <div className="space-y-1"><Label>Username</Label><Input value={username} onChange={(e) => setUsername(e.target.value)} placeholder="username" data-testid="login-username-input" /></div>
            <div className="space-y-1"><Label>Password</Label><Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="password" data-testid="login-password-input" /></div>
            <Button type="submit" variant="secondary" className="w-full h-11 gap-2" disabled={loading || !username || !password} data-testid="password-login-button">
              {loading && <Loader2 size={16} className="animate-spin" />} Sign in
            </Button>
          </form>
          <p className="text-xs text-muted-foreground text-center">
            Managers use Google · Trainers &amp; Trainees use the username from your admin.
          </p>
        </div>
      </div>
    </div>
  );
}
